import { useState, useEffect } from 'react';
import VolunteerList from './VolunteerList';
import TaskList from './TaskList';
import TaskForm from './TaskForm';
import AdminProjects from './AdminProjects';
import SkillsList from './SkillsList';
import LocationsList from './LocationsList';
import DonationsList from './DonationsList';
import { useParams, useNavigate } from 'react-router-dom';
import TableSelector from './TableSelector';
import './astyles/ManageTables.css'

type TabType = 'volunteers' | 'tasks' | 'projects' | 'skills' | 'locations' | 'donations';

// Icon components for tabs
const VolunteersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ProjectsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const SkillsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const LocationsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const DonationsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

export default function ManageRecords() {
    const { tab } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>(tab as TabType || 'volunteers');
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [showProjectForm, setShowProjectForm] = useState(false);
  
    useEffect(() => {
      if (tab && ['volunteers', 'tasks', 'projects', 'skills', 'locations', 'donations'].includes(tab)) {
        setActiveTab(tab as TabType);
      }
    }, [tab]);
  
    const handleTabChange = (newTab: TabType) => {
      setActiveTab(newTab);
      navigate(`/admin/manage-records/${newTab}`);
      setShowTaskForm(false);
      setShowProjectForm(false);
    };

    const getTabIcon = (tabName: TabType) => {
      switch(tabName) {
        case 'volunteers': return <VolunteersIcon />;
        case 'projects': return <ProjectsIcon />;
        case 'skills': return <SkillsIcon />;
        case 'locations': return <LocationsIcon />;
        case 'donations': return <DonationsIcon />;
        default: return null;
      }
    };

    const getTabLabel = (tabName: TabType) => {
      return tabName.charAt(0).toUpperCase() + tabName.slice(1);
    };
  
    if (!tab) {
      return <TableSelector />;
    }
    
    return (
      <div className="manage-records-redesigned">
        {/* Header Section */}
        <div className="records-header">
          <div className="header-content">
            <h1 className="page-title">Manage Records</h1>
            <p className="page-subtitle">View and manage all your platform data in one place</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="table-tabs-redesigned">
          <div className="tabs-container">
            {(['volunteers', 'projects', 'skills', 'locations', 'donations'] as TabType[]).map((tabName) => (
              <button
                key={tabName}
                className={`tab-button ${activeTab === tabName ? 'active' : ''}`}
                onClick={() => handleTabChange(tabName)}
              >
                <span className="tab-icon">{getTabIcon(tabName)}</span>
                <span className="tab-label">{getTabLabel(tabName)}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Content Area */}
        <div className="table-content-redesigned">
          <div className="content-wrapper">
            {activeTab === 'volunteers' && <VolunteerList />}
            
            {activeTab === 'projects' && <AdminProjects />}
            
            {activeTab === 'tasks' && (
              <>
                {!showTaskForm && (
                  <button
                    className="create-btn-redesigned"
                    onClick={() => setShowTaskForm(true)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create New Task
                  </button>
                )}
                {showTaskForm
                  ? <TaskForm onSuccess={() => setShowTaskForm(false)} />
                  : <TaskList />}
              </>
            )}
            
            {activeTab === 'skills' && <SkillsList />}
            
            {activeTab === 'locations' && <LocationsList />}
            
            {activeTab === 'donations' && <DonationsList />}
          </div>
        </div>
      </div>
    );
  }